<template>
  <main class="app-main contracts-page">
    <div class="app-content-header">
      <div class="container-fluid">
        <ol class="breadcrumb mb-3">
          <li class="breadcrumb-item"><router-link to="/">الرئيسية</router-link></li>
          <li v-if="canRead" class="breadcrumb-item">
            <router-link to="/contracts">منصة العقود العقارية</router-link>
          </li>
          <li class="breadcrumb-item active">{{ isEdit ? 'تعديل العقد' : 'إضافة عقد جديد' }}</li>
        </ol>
        <div class="contract-heading">
          <div>
            <h1>{{ isEdit ? 'تعديل العقد العقاري' : 'إضافة عقد عقاري جديد' }}</h1>
            <p>{{ original?.code || 'أدخل بيانات العقد وأرفق نسخته المعتمدة' }}</p>
          </div>
          <div class="contract-actions">
            <router-link v-if="canRead" to="/contracts" class="btn btn-outline-primary">سجل العقود</router-link>
            <router-link to="/contracts/templates" class="btn btn-outline-primary">مكتبة النماذج</router-link>
          </div>
        </div>
      </div>
    </div>
    <div class="app-content">
      <div class="container-fluid">
        <div v-if="!canSave" class="contract-card contract-access-denied">
          ليس لديك صلاحية {{ isEdit ? 'تعديل' : 'إضافة' }} العقود.
        </div>
        <template v-else>
          <div v-if="loading" class="py-4"><mainLoader /></div>
          <template v-else>
            <errorAlert v-if="hasError" :alertMessage="message" :errorNumber="errorNumber" />
            <div v-if="savedCode" class="alert alert-success" role="status">
              تم حفظ العقد {{ savedCode }} بنجاح. يمكنك إضافة عقد آخر.
            </div>
            <div v-if="loadFailed" class="contract-card contract-empty">
              <h3>تعذر تحميل بيانات النموذج</h3>
              <button type="button" class="btn btn-outline-primary" @click="load">إعادة المحاولة</button>
            </div>
            <form v-else @submit.prevent="submitForm">
              <fieldset :disabled="saving" class="border-0 p-0 m-0">
                <div class="contract-form-grid">
                  <div class="contract-form-main">
                    <section class="contract-card" aria-labelledby="contract-basic-title">
                      <div class="contract-card-header">
                        <h2 id="contract-basic-title">
                          <i class="bi bi-file-earmark-text ms-2" aria-hidden="true"></i>
                          بيانات العقد
                        </h2>
                        <span class="contract-secondary">الحقول بعلامة * مطلوبة</span>
                      </div>
                      <div class="contract-card-body">
                        <div class="row g-3">
                          <div class="col-12 col-md-6">
                            <label for="contract-type" class="form-label">
                              نوع العقد
                              <span class="text-danger">*</span>
                            </label>
                            <select
                              id="contract-type"
                              v-model="form.typeId"
                              class="form-select"
                              required
                              @change="typeChanged"
                            >
                              <option value="" disabled>اختر نوع العقد</option>
                              <option v-for="type in types" :key="type.id" :value="String(type.id)">
                                {{ type.name }}
                              </option>
                            </select>
                          </div>
                          <div class="col-12 col-md-6">
                            <label for="contract-status" class="form-label">
                              الحالة
                              <span class="text-danger">*</span>
                            </label>
                            <select id="contract-status" v-model="form.status" class="form-select" required>
                              <option v-for="status in statuses" :key="status.value" :value="status.value">
                                {{ status.label }}
                              </option>
                            </select>
                          </div>
                          <div class="col-12">
                            <label for="contract-title" class="form-label">
                              اسم العقد / عنوان الملف
                              <span class="text-danger">*</span>
                            </label>
                            <input
                              id="contract-title"
                              v-model.trim="form.title"
                              class="form-control"
                              required
                              maxlength="255"
                              placeholder="عنوان يوضح موضوع العقد"
                            />
                          </div>
                          <div v-if="isManagement" class="col-12">
                            <div class="form-check">
                              <input
                                id="contract-has-annex"
                                v-model="form.hasAnnex"
                                type="checkbox"
                                class="form-check-input"
                              />
                              <label for="contract-has-annex" class="form-check-label">
                                يوجد ملحق لعقد إدارة الأملاك
                              </label>
                            </div>
                            <p class="contract-hint mt-2 mb-0">
                              حدد هذا الخيار إذا كان الاتفاق يتضمن ملحقاً. يمكن إضافة الملحق كعقد مرتبط.
                            </p>
                          </div>
                          <div v-if="isAnnex" class="col-12">
                            <label for="contract-parent" class="form-label">
                              عقد إدارة الأملاك الأساسي
                              <span class="text-danger">*</span>
                            </label>
                            <select
                              id="contract-parent"
                              v-model="form.parentContractId"
                              class="form-select"
                              required
                              @change="parentChanged"
                            >
                              <option value="" disabled>اختر العقد الأساسي</option>
                              <option v-for="parent in managementContracts" :key="parent.id" :value="String(parent.id)">
                                {{ parent.code }} — {{ parent.title }} — {{ parent.clientName }}
                              </option>
                            </select>
                            <p class="contract-hint mt-2 mb-0">
                              تُملأ بيانات العميل والموظف من العقد الأساسي، ويجب أن يكون الملحق لنفس العميل.
                            </p>
                            <p v-if="!managementContracts.length" class="text-warning small mt-2 mb-0">
                              يلزم إنشاء عقد إدارة أملاك أولاً لإضافة ملحق.
                            </p>
                          </div>
                        </div>
                        <p class="contract-hint mt-3 mb-0">
                          تُحسب حالة الانتهاء تلقائياً من تاريخ نهاية العقد، ويُعد العقد قريب الانتهاء خلال آخر 60
                          يوماً.
                        </p>
                      </div>
                    </section>
                    <section class="contract-card" aria-labelledby="contract-parties-title">
                      <div class="contract-card-header">
                        <h2 id="contract-parties-title">
                          <i class="bi bi-people ms-2" aria-hidden="true"></i>
                          أطراف العقد
                        </h2>
                      </div>
                      <div class="contract-card-body">
                        <div class="row g-4">
                          <div class="col-12 col-md-6">
                            <PhoneLookup
                              :key="`client-${lookupKey}`"
                              v-model="client"
                              input-id="contract-client-mobile"
                              label="العميل / الطرف الثاني"
                              :disabled="saving"
                            />
                            <p v-if="client && !client.id" class="contract-hint mt-2 mb-0">
                              بيانات تاريخية محفوظة لعميل لم يعد حسابه متاحاً.
                            </p>
                          </div>
                          <div class="col-12 col-md-6">
                            <label for="contract-employee-admin" class="form-label">
                              جالب العقد (الموظف المسؤول) <span class="text-danger">*</span>
                            </label>
                            <AdminSelect
                              input-id="contract-employee-admin"
                              :model-value="employee?.id ? String(employee.id) : ''"
                              :options="admins"
                              :historical="employee"
                              :disabled="saving"
                              placeholder="ابحث عن الموظف من الأدمنز"
                              @update:model-value="selectEmployee"
                            />
                            <p v-if="employee && !employee.id" class="contract-hint mt-2 mb-0">
                              بيانات تاريخية محفوظة لموظف لم يعد سجله متاحاً.
                            </p>
                          </div>
                        </div>
                      </div>
                    </section>
                    <section class="contract-card" aria-labelledby="contract-period-title">
                      <div class="contract-card-header">
                        <h2 id="contract-period-title">
                          <i class="bi bi-calendar3 ms-2" aria-hidden="true"></i>
                          المدة والتفاصيل
                        </h2>
                      </div>
                      <div class="contract-card-body">
                        <div class="row g-3">
                          <div class="col-12 col-sm-6">
                            <label for="contract-start" class="form-label">
                              تاريخ البداية
                              <span class="text-danger">*</span>
                            </label>
                            <input
                              id="contract-start"
                              v-model="form.startDate"
                              type="date"
                              class="form-control"
                              required
                              :max="form.endDate || undefined"
                            />
                          </div>
                          <div class="col-12 col-sm-6">
                            <label for="contract-end" class="form-label">
                              تاريخ النهاية
                              <span class="text-danger">*</span>
                            </label>
                            <input
                              id="contract-end"
                              v-model="form.endDate"
                              type="date"
                              class="form-control"
                              required
                              :min="form.startDate || undefined"
                            />
                          </div>
                          <div class="col-12 col-sm-6">
                            <label for="contract-properties-count" class="form-label">
                              عدد العقارات
                              <span class="text-danger">*</span>
                            </label>
                            <input
                              id="contract-properties-count"
                              v-model="form.propertiesCount"
                              type="number"
                              min="1"
                              step="1"
                              class="form-control"
                              required
                            />
                          </div>
                          <div class="col-12 col-sm-6">
                            <label for="contract-percentage" class="form-label">
                              النسبة المتفق عليها (%)
                              <span class="text-danger">*</span>
                            </label>
                            <div class="input-group">
                              <input
                                id="contract-percentage"
                                v-model="form.percentage"
                                type="number"
                                min="0"
                                max="100"
                                step="0.0001"
                                class="form-control"
                                required
                              />
                              <span class="input-group-text">%</span>
                            </div>
                          </div>
                          <div class="col-12">
                            <label for="contract-notes" class="form-label">الملاحظات</label>
                            <textarea
                              id="contract-notes"
                              v-model="form.notes"
                              rows="4"
                              maxlength="10000"
                              class="form-control"
                              placeholder="ملاحظات إضافية مرتبطة بالعقد"
                            ></textarea>
                          </div>
                        </div>
                      </div>
                    </section>
                  </div>
                  <aside class="contract-form-aside">
                    <section class="contract-card mb-4" aria-labelledby="contract-attachment-title">
                      <div class="contract-card-header"><h2 id="contract-attachment-title">مرفق العقد</h2></div>
                      <div class="contract-card-body">
                        <div class="contract-file-box text-center">
                          <i class="bi bi-cloud-arrow-up" aria-hidden="true"></i>
                          <p class="mb-2 mt-2">أرفق نسخة العقد</p>
                          <label for="contract-attachment" class="visually-hidden">اختيار مرفق العقد</label>
                          <input
                            id="contract-attachment"
                            :key="fileInputKey"
                            type="file"
                            class="form-control form-control-sm"
                            accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp"
                            @change="selectFile"
                          />
                          <p class="contract-hint mt-2 mb-0">
                            PDF أو Word (.doc / .docx) أو صورة
                            <br />
                            الحد الأقصى 15 MB
                          </p>
                        </div>
                        <div v-if="attachment" class="mt-3">
                          <div class="contract-file-name fw-bold">{{ attachment.name }}</div>
                          <div class="contract-hint">{{ fileSize(attachment.size) }}</div>
                          <img
                            v-if="localPreview && attachmentKind(attachment) === 'image'"
                            :src="localPreview"
                            alt="معاينة المرفق الجديد"
                            class="img-fluid rounded mt-2"
                          />
                          <button
                            type="button"
                            class="btn btn-link btn-sm text-danger p-0 mt-2"
                            @click="clearSelectedFile"
                          >
                            إزالة الملف الجديد
                          </button>
                        </div>
                        <div v-else-if="original?.attachment && !form.removeAttachment" class="mt-3">
                          <div class="contract-file-name">{{ original.attachment.name }}</div>
                          <div class="contract-hint">المرفق المحفوظ · {{ fileSize(original.attachment.size) }}</div>
                          <div class="d-flex gap-2 mt-2">
                            <button
                              type="button"
                              class="btn btn-outline-primary btn-sm"
                              :disabled="downloading"
                              @click="downloadExisting"
                            >
                              تنزيل
                            </button>
                            <button
                              type="button"
                              class="btn btn-outline-danger btn-sm"
                              @click="form.removeAttachment = true"
                            >
                              إزالة المرفق
                            </button>
                          </div>
                        </div>
                        <div v-else-if="original?.attachment && form.removeAttachment" class="contract-hint mt-3">
                          سيُحذف المرفق عند حفظ التعديلات.
                          <button type="button" class="btn btn-link btn-sm p-0" @click="form.removeAttachment = false">
                            تراجع
                          </button>
                        </div>
                      </div>
                    </section>
                    <section v-if="selectedType" class="contract-card mb-4" aria-labelledby="contract-template-title">
                      <div class="contract-card-header"><h2 id="contract-template-title">نموذج العقد</h2></div>
                      <div class="contract-card-body">
                        <p class="small">{{ selectedType.name }}</p>
                        <p v-if="selectedType.description" class="contract-hint">{{ selectedType.description }}</p>
                        <button
                          v-if="selectedType.template"
                          type="button"
                          class="btn btn-outline-primary w-100"
                          :disabled="templateDownloading"
                          @click="downloadTemplate"
                        >
                          {{ templateDownloading ? 'جارٍ التنزيل...' : 'تنزيل النموذج' }}
                        </button>
                        <p v-else class="contract-hint mb-0">
                          لم يُرفع نموذج لهذا النوع بعد. يمكن للموظف المخوّل إضافته من مكتبة النماذج.
                        </p>
                      </div>
                    </section>
                    <div class="d-grid gap-2">
                      <button type="submit" class="btn btn-primary py-2" :disabled="saving">
                        <span v-if="saving" class="spinner-border spinner-border-sm ms-2" aria-hidden="true"></span>
                        {{ saving ? 'جارٍ حفظ العقد...' : isEdit ? 'حفظ التعديلات' : 'إضافة العقد' }}
                      </button>
                      <router-link
                        v-if="canRead"
                        :to="isEdit ? `/contracts/view/${contractId}` : '/contracts'"
                        class="btn btn-outline-secondary"
                      >
                        إلغاء
                      </router-link>
                    </div>
                  </aside>
                </div>
              </fieldset>
            </form>
          </template>
        </template>
      </div>
    </div>
  </main>
</template>

<script>
import { checkAccess, toast } from '@/helpers/tools';
import PhoneLookup from './PhoneLookup.vue';
import AdminSelect from '@/components/AdminSelect.vue';
import {
  contractStatuses,
  fileSize,
  attachmentKind,
  validateFile,
  authenticatedDownload,
  errorMessage,
} from './helpers';
import './contracts.css';

const emptyForm = () => ({
  title: '',
  typeId: '',
  propertiesCount: 1,
  startDate: '',
  endDate: '',
  percentage: 0,
  notes: '',
  status: 'active',
  hasAnnex: false,
  parentContractId: '',
  removeAttachment: false,
});
export default {
  name: 'ContractForm',
  components: { PhoneLookup, AdminSelect },
  props: {
    mode: { type: String, required: true },
    contractId: { type: [String, Number], default: null },
  },
  data() {
    return {
      form: emptyForm(),
      original: null,
      client: null,
      employee: null,
      admins: [],
      types: [],
      statuses: contractStatuses,
      managementContracts: [],
      attachment: null,
      localPreview: '',
      lookupKey: 0,
      fileInputKey: 0,
      savedCode: '',
      loading: true,
      loadFailed: false,
      saving: false,
      downloading: false,
      templateDownloading: false,
      hasError: false,
      message: '',
      errorNumber: 0,
      serial: 0,
    };
  },
  computed: {
    isEdit() {
      return this.mode === 'update';
    },
    canRead() {
      return checkAccess('contracts_read');
    },
    canSave() {
      return checkAccess(this.isEdit ? 'contracts_update' : 'contracts_create');
    },
    selectedType() {
      return this.types.find((type) => String(type.id) === String(this.form.typeId));
    },
    isManagement() {
      return this.selectedType?.key === 'property_management';
    },
    isAnnex() {
      return this.selectedType?.key === 'management_annex';
    },
  },
  watch: {
    contractId() {
      this.load();
    },
    '$route.query'() {
      if (!this.isEdit) this.load();
    },
  },
  methods: {
    fileSize,
    attachmentKind,
    selectEmployee(id) {
      this.employee = this.admins.find(admin => String(admin.id) === String(id)) || null;
    },
    reset() {
      this.saving = false;
      this.form = emptyForm();
      this.original = null;
      this.client = null;
      this.employee = null;
      this.lookupKey += 1;
      this.clearSelectedFile();
      this.savedCode = '';
      this.hasError = false;
      this.loadFailed = false;
    },
    async load() {
      const serial = ++this.serial;
      this.reset();
      if (!this.canSave) {
        this.loading = false;
        return;
      }
      this.loading = true;
      const id = this.contractId;
      try {
        const [optionsResponse, recordResponse] = await Promise.all([
          this.$http.get('/api/admin/contracts/options'),
          id ? this.$http.get(`/api/admin/contracts/retrieve/${id}`) : Promise.resolve(null),
        ]);
        if (serial !== this.serial) return;
        const options = optionsResponse.data;
        this.types = Array.isArray(options.types) ? options.types : [];
        this.admins = Array.isArray(options.admins) ? options.admins : [];
        this.statuses = options.statuses?.length ? options.statuses : contractStatuses;
        this.managementContracts = Array.isArray(options.managementContracts) ? options.managementContracts : [];
        if (recordResponse) {
          const row = recordResponse.data.row;
          this.original = row;
          for (const key of Object.keys(this.form))
            if (row[key] !== undefined && row[key] !== null) this.form[key] = row[key];
          this.form.typeId = String(row.typeId || '');
          this.form.parentContractId = row.parentContractId ? String(row.parentContractId) : '';
          this.form.hasAnnex = Boolean(row.hasAnnex);
          this.form.removeAttachment = false;
          this.client = { id: row.clientId, name: row.clientName, mobile: row.clientMobile, historical: true };
          this.employee = {
            id: row.employeeId,
            name: row.employeeName,
            mobileNumber: row.employeeMobile,
            historical: true,
          };
          if (
            row.parentContractId &&
            !this.managementContracts.some((parent) => String(parent.id) === String(row.parentContractId))
          )
            this.managementContracts.push({
              id: row.parentContractId,
              code: row.parentContractCode,
              title: row.parentContractTitle,
              clientId: row.clientId,
              clientName: row.clientName,
              clientMobile: row.clientMobile,
              employeeId: row.employeeId,
              employeeName: row.employeeName,
              employeeMobile: row.employeeMobile,
            });
        } else if (this.$route.query.type) {
          const type = this.types.find((item) => item.key === this.$route.query.type);
          if (type) this.form.typeId = String(type.id);
          const parent = this.managementContracts.find(
            (item) => String(item.id) === String(this.$route.query.parentContractId)
          );
          if (this.isAnnex && parent) {
            this.form.parentContractId = String(parent.id);
            this.parentChanged();
          }
        }
        this.lookupKey += 1;
      } catch (error) {
        if (serial !== this.serial) return;
        [this.message, this.errorNumber] = this.$globalError(error, true);
        this.hasError = true;
        this.loadFailed = true;
      } finally {
        if (serial === this.serial) this.loading = false;
      }
    },
    typeChanged() {
      if (!this.isManagement) this.form.hasAnnex = false;
      if (!this.isAnnex) this.form.parentContractId = '';
    },
    parentChanged() {
      const parent = this.managementContracts.find((item) => String(item.id) === String(this.form.parentContractId));
      if (!parent) return;
      this.client = { id: parent.clientId, name: parent.clientName, mobile: parent.clientMobile };
      this.employee = { id: parent.employeeId, name: parent.employeeName, mobileNumber: parent.employeeMobile };
      if (parent.propertiesCount) this.form.propertiesCount = parent.propertiesCount;
      if (parent.startDate) this.form.startDate = parent.startDate;
      if (parent.endDate) this.form.endDate = parent.endDate;
      this.lookupKey += 1;
    },
    selectFile(event) {
      const file = event.target.files?.[0];
      if (!file) return;
      const validation = validateFile(file);
      if (validation) {
        toast(validation);
        event.target.value = '';
        return;
      }
      this.clearSelectedFile();
      this.attachment = file;
      this.form.removeAttachment = false;
      if (attachmentKind({ name: file.name, mimeType: file.type }) === 'image')
        this.localPreview = URL.createObjectURL(file);
    },
    clearSelectedFile() {
      if (this.localPreview) URL.revokeObjectURL(this.localPreview);
      this.localPreview = '';
      this.attachment = null;
      this.fileInputKey += 1;
    },
    async downloadExisting() {
      if (!this.original?.attachment || this.downloading) return;
      this.downloading = true;
      try {
        await authenticatedDownload(
          this.$http,
          `/api/admin/contracts/attachment/${this.original.id}`,
          this.original.attachment.name
        );
      } catch (error) {
        toast(await errorMessage(this, error));
      } finally {
        this.downloading = false;
      }
    },
    async downloadTemplate() {
      if (!this.selectedType?.template || this.templateDownloading) return;
      this.templateDownloading = true;
      try {
        await authenticatedDownload(
          this.$http,
          `/api/admin/contracts/templates/${this.selectedType.id}/download`,
          this.selectedType.template.name
        );
      } catch (error) {
        toast(await errorMessage(this, error));
      } finally {
        this.templateDownloading = false;
      }
    },
    validationMessage() {
      if (!this.form.title.trim() || !this.selectedType) return 'أدخل عنوان العقد واختر نوعه';
      if (
        !this.client ||
        (!this.client.id && !(this.isEdit && this.client.historical && this.original?.clientId == null))
      )
        return 'حدد العميل عن طريق رقم الجوال أولاً';
      if (
        !this.employee ||
        (!this.employee.id && !(this.isEdit && this.employee.historical && this.original?.employeeId == null))
      )
        return 'اختر الموظف المسؤول من قائمة الأدمنز أولاً';
      if (!this.form.startDate || !this.form.endDate || this.form.endDate < this.form.startDate)
        return 'أدخل فترة صحيحة للعقد، بحيث لا تسبق النهاية تاريخ البداية';
      if (!Number.isInteger(Number(this.form.propertiesCount)) || Number(this.form.propertiesCount) < 1)
        return 'عدد العقارات يجب أن يكون عدداً صحيحاً لا يقل عن واحد';
      if (
        this.form.percentage === '' ||
        !Number.isFinite(Number(this.form.percentage)) ||
        Number(this.form.percentage) < 0 ||
        Number(this.form.percentage) > 100
      )
        return 'النسبة يجب أن تكون بين 0 و100';
      if (this.isAnnex && !this.form.parentContractId) return 'اختر عقد إدارة الأملاك الأساسي';
      if (this.isAnnex) {
        const parent = this.managementContracts.find((item) => String(item.id) === String(this.form.parentContractId));
        if (parent && String(parent.clientId) !== String(this.client.id))
          return 'يجب أن يكون عميل الملحق هو عميل العقد الأساسي نفسه';
      }
      return '';
    },
    async submitForm() {
      if (this.saving || !this.canSave) return;
      this.hasError = false;
      const validation = this.validationMessage();
      if (validation) {
        this.message = validation;
        this.errorNumber = 0;
        this.hasError = true;
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      this.saving = true;
      const serial = this.serial;
      const id = this.contractId;
      const input = {
        ...this.form,
        title: this.form.title.trim(),
        typeId: Number(this.form.typeId),
        clientId: this.client.id || null,
        employeeId: this.employee.id || null,
        propertiesCount: Number(this.form.propertiesCount),
        percentage: Number(this.form.percentage),
        hasAnnex: this.isManagement && this.form.hasAnnex,
        parentContractId: this.isAnnex ? Number(this.form.parentContractId) : null,
      };
      const body = new FormData();
      body.append('data', JSON.stringify(input));
      if (this.attachment) body.append('attachment', this.attachment);
      try {
        const { data } = id
          ? await this.$http.put(`/api/admin/contracts/update/${id}`, body)
          : await this.$http.post('/api/admin/contracts/create', body);
        if (serial !== this.serial) return;
        toast(id ? 'تم حفظ تعديلات العقد' : 'تم إضافة العقد بنجاح', 'success');
        if (this.canRead) await this.$router.push(`/contracts/view/${data.row.id}`);
        else if (checkAccess('contracts_update')) {
          if (id && String(id) === String(data.row.id)) await this.load();
          else await this.$router.push(`/contracts/update/${data.row.id}`);
        } else {
          const code = data.row.code;
          this.reset();
          this.savedCode = code;
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      } catch (error) {
        if (serial !== this.serial) return;
        [this.message, this.errorNumber] = this.$globalError(error, true);
        this.hasError = true;
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } finally {
        if (serial === this.serial) this.saving = false;
      }
    },
  },
  created() {
    this.load();
  },
  beforeUnmount() {
    this.serial += 1;
    if (this.localPreview) URL.revokeObjectURL(this.localPreview);
  },
};
</script>
